/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Building2, Globe, Phone, Mail, MapPin, ArrowLeft, Calendar,
  Facebook, Instagram, Linkedin, Twitter, ExternalLink, Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MembershipSeal } from '../components/MembershipSeal';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const industryLabels = {
  restaurants: 'Restaurants & Hospitality', professional: 'Professional Services', health: 'Health & Wellness',
  real_estate: 'Real Estate', finance: 'Finance', marketing: 'Marketing & Media', technology: 'Technology',
  education: 'Education', retail: 'Retail', community: 'Community Organizations', other: 'Other'
};

const BusinessProfilePage = () => {
  const { businessId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (businessId) fetchProfile();
  }, [businessId]);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/public/business/${businessId}`);
      setData(res.data);
    } catch { setError(true); } finally { setLoading(false); }
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>;

  if (error || !data) return (
    <div className="min-h-screen pt-20 flex flex-col items-center justify-center px-4">
      <Building2 className="w-16 h-16 text-slate-300 mb-4" />
      <h2 className="text-xl font-bold text-[#002249] mb-2">Business Not Found</h2>
      <p className="text-slate-500 mb-6">This business profile is not available or has been hidden.</p>
      <Link to="/members"><Button className="bg-[#002249] hover:bg-[#001630] rounded-lg">Back to Directory</Button></Link>
    </div>
  );

  const { profile: p, membership_plan: plan, badges = [], member_since } = data;

  return (
    <div className="min-h-screen pt-16 sm:pt-20 bg-[#F8F9FB]" data-testid="business-profile-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#002249] via-[#1D3557] to-[#457B9D] pt-8 sm:pt-12 pb-24 sm:pb-28 px-5 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/members" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-directory">
            <ArrowLeft className="w-4 h-4" /> Back to Directory
          </Link>
        </div>
      </div>

      {/* Profile Card */}
      <div className="max-w-4xl mx-auto px-5 sm:px-8 -mt-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header — logo/info left, seal right */}
          <div className="relative p-6 sm:p-8">
            {/* Membership Seal — top right */}
            {plan && (
              <div className="absolute top-5 right-5 sm:top-6 sm:right-8 z-10">
                <MembershipSeal plan={plan} />
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6 pr-0 sm:pr-28">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden p-2">
                {p.logo_url ? (
                  <Img variant="business" src={resolveImg(p.logo_url)} alt={p.company_name} className="max-w-full max-h-full object-contain" />
                ) : (
                  <Building2 className="w-10 h-10 text-slate-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-[#002249] leading-tight" data-testid="business-name">{p.company_name}</h1>
                {p.slogan && <p className="text-[14px] text-slate-500 italic mt-1">"{p.slogan}"</p>}
                <div className="flex items-center flex-wrap gap-2 mt-3">
                  {badges.includes('founding_member') && (
                    <span className="inline-flex items-center gap-1 text-[12px] font-bold bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200 px-3 py-1 rounded-full shadow-sm" data-testid="founding-member-badge">
                      <Award className="w-3.5 h-3.5" /> Founding Member
                    </span>
                  )}
                  {p.industry && <span className="text-[12px] font-medium bg-[#002249]/10 text-[#002249] px-2.5 py-0.5 rounded-full">{industryLabels[p.industry] || p.industry}</span>}
                  {member_since && <span className="text-[12px] text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" />Since {new Date(member_since).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {p.description && (
            <div className="px-6 sm:px-8 pb-6">
              <h3 className="text-[13px] font-bold text-slate-400 uppercase tracking-wider mb-2">About</h3>
              <p className="text-[15px] text-slate-700 leading-relaxed">{p.description}</p>
            </div>
          )}

          {/* Mission & Vision */}
          {(p.mission || p.vision) && (
            <div className="px-6 sm:px-8 pb-6 grid sm:grid-cols-2 gap-4">
              {p.mission && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="text-[12px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Mission</h4>
                  <p className="text-[14px] text-slate-700 leading-relaxed">{p.mission}</p>
                </div>
              )}
              {p.vision && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <h4 className="text-[12px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Vision</h4>
                  <p className="text-[14px] text-slate-700 leading-relaxed">{p.vision}</p>
                </div>
              )}
            </div>
          )}

          {/* Contact */}
          <div className="px-6 sm:px-8 pb-6 sm:pb-8">
            <h3 className="text-[13px] font-bold text-slate-400 uppercase tracking-wider mb-3">Contact Information</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {p.address && <InfoRow icon={MapPin} text={p.address} />}
              {p.phone && <InfoRow icon={Phone} text={p.phone} href={`tel:${p.phone}`} />}
              {p.email && <InfoRow icon={Mail} text={p.email} href={`mailto:${p.email}`} />}
              {p.website && <InfoRow icon={Globe} text={p.website} href={p.website.startsWith('http') ? p.website : `https://${p.website}`} external />}
            </div>
          </div>

          {/* Social */}
          {(p.social_facebook || p.social_instagram || p.social_linkedin || p.social_twitter) && (
            <div className="px-6 sm:px-8 pb-6 sm:pb-8 border-t border-slate-100 pt-5">
              <h3 className="text-[13px] font-bold text-slate-400 uppercase tracking-wider mb-3">Follow Us</h3>
              <div className="flex items-center gap-3">
                {p.social_facebook && <SocialBtn href={p.social_facebook} icon={Facebook} color="bg-blue-50 text-blue-600 hover:bg-blue-100" />}
                {p.social_instagram && <SocialBtn href={p.social_instagram} icon={Instagram} color="bg-pink-50 text-pink-600 hover:bg-pink-100" />}
                {p.social_linkedin && <SocialBtn href={p.social_linkedin} icon={Linkedin} color="bg-blue-50 text-blue-700 hover:bg-blue-100" />}
                {p.social_twitter && <SocialBtn href={p.social_twitter} icon={Twitter} color="bg-sky-50 text-sky-600 hover:bg-sky-100" />}
              </div>
            </div>
          )}

          {/* Gallery */}
          {p.gallery_images?.length > 0 && (
            <div className="px-6 sm:px-8 pb-6 sm:pb-8 border-t border-slate-100 pt-5">
              <h3 className="text-[13px] font-bold text-slate-400 uppercase tracking-wider mb-3">Gallery</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {p.gallery_images.map((img, i) => (
                  <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <Img variant="generic" src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <div className="text-center py-8">
          <Link to="/membership"><Button className="bg-[#002249] hover:bg-[#001630] rounded-lg">Join the DCCF</Button></Link>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, text, href, external }) => (
  <div className="flex items-center gap-2.5 text-[14px] text-slate-600">
    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
    {href ? (
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined} className="hover:text-[#002249] transition-colors flex items-center gap-1">
        {text} {external && <ExternalLink className="w-3 h-3" />}
      </a>
    ) : <span>{text}</span>}
  </div>
);

const SocialBtn = ({ href, icon: Icon, color }) => (
  <a href={href.startsWith('http') ? href : `https://${href}`} target="_blank" rel="noopener noreferrer"
    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${color}`}>
    <Icon className="w-4 h-4" />
  </a>
);

export default BusinessProfilePage;
